/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/hashing_algorithms/KavenMD5.ts
 * @create:      2019-04-05 07:31:11.304
 * @modify:      2025-07-01 13:13:47.350
 * @version:     6.0.0
 * @times:       77
 * @lines:       373
 * @copyright:   Copyright © 2019-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { ArraySplit } from "../array/ArraySplit";
import { ArrayToHEX } from "../array/ArrayToHEX";
import { BitString } from "../class/BitString";
import { InitializeAsyncDataAcquisition, InitializeSyncDataAcquisition } from "../Initialize";
import { LeftRotate } from "../number/LeftRotate";
import { Swap32 } from "../number/Swap32";
import { ToUInt32 } from "../number/ToUInt32";
import { TAsyncHashInput, TSyncHashInput } from "../type/advanced";

/**
 * @see https://en.wikipedia.org/wiki/MD5
 * @version 2021-12-07
 */
export class KavenMD5 {
    public Compute(input: TSyncHashInput): string {

        // Note: All variables are unsigned 32 bit and wrap modulo 2^32 when calculating

        const data = InitializeSyncDataAcquisition(input);

        // s specifies the per-round shift amounts
        const s = [
            7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
            5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
            4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
            6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
        ];

        // Use binary integer part of the sines of integers (Radians) as constants
        // const K: number[] = new Array(64);
        // for (let i = 0; i <= 63; i++) {
        //     K[i] = Math.floor(Math.pow(2, 32) * Math.abs(Math.sin(i + 1)));
        // }

        // Or just use the following precomputed table
        const K = [
            0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee,
            0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
            0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be,
            0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
            0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa,
            0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
            0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed,
            0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
            0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c,
            0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
            0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05,
            0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
            0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039,
            0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
            0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1,
            0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
        ];

        // Initialize variables
        let a0 = 0x67452301;
        let b0 = 0xefcdab89;
        let c0 = 0x98badcfe;
        let d0 = 0x10325476;

        /// Pre-processing

        const L = data.TotalLengthInBytes * 8;

        const paddingBytes: number[] = [];

        // append a single '1' bit, where the first bit is the most significant bit of the byte
        paddingBytes.push(0x80); // 8 bits

        // padding with zeros
        const total = 512 - (L % 512);
        let bitsCount = total - 8 - 64;
        // #38
        if (bitsCount < 0) {
            bitsCount += 512;
        }

        let n = bitsCount / 8;
        while (n-- > 0) {
            paddingBytes.push(0);
        }

        // append original length in bits
        const lbe = new BitString(L).ToBytes(64);
        paddingBytes.push(...lbe.reverse());

        const totalSize = data.TotalLengthInBytes + paddingBytes.length;

        /// Process the message in successive 512-bit chunks
        const totalChunks = totalSize / 64;

        for (let i = 0; i < totalChunks; i++) {
            const chunk = data.Acquire(i * 64, 64);

            while (chunk.length < 64) {
                chunk.push(paddingBytes.shift()!);
            }

            const M: number[] = new Array(64);

            // break chunk into sixteen 32-bit words M[j], 0 ≤ j ≤ 15
            const bit32Array = ArraySplit(chunk, 4, true);
            for (let j = 0; j < 16; j++) {
                const bits32 = bit32Array[j];
                M[j] = bits32[3] << 24 | bits32[2] << 16 | bits32[1] << 8 | bits32[0];
            }

            // Initialize hash value for this chunk
            let A = a0;
            let B = b0;
            let C = c0;
            let D = d0;

            // Main loop
            for (let i = 0; i <= 63; i++) {
                let F: number;
                let g: number;

                if (i >= 0 && i <= 15) {
                    // F := (B and C) or ((not B) and D)
                    F = (B & C) | ((~B) & D);

                    // g := i
                    g = i;
                } else if (i >= 16 && i <= 31) {
                    // F := (D and B) or ((not D) and C)
                    F = (D & B) | ((~D) & C);

                    // g := (5×i + 1) mod 16
                    g = (5 * i + 1) % 16;
                } else if (i >= 32 && i <= 47) {
                    // F := B xor C xor D
                    F = B ^ C ^ D;

                    // g := (3×i + 5) mod 16
                    g = (3 * i + 5) % 16;
                    // } else if (i >= 48 && i <= 63) {
                } else {
                    // F := C xor (B or (not D))
                    F = C ^ (B | (~D));

                    // g := (7×i) mod 16
                    g = (7 * i) % 16;
                }

                // Be wary of the below definitions of a,b,c,d
                F = this.addMany(F, A, K[i], M[g]);
                A = D;
                D = C;
                C = B;
                B = this.add(B, LeftRotate(F, s[i]));
            }

            // Add this chunk's hash to result so far
            a0 = this.add(a0, A);
            b0 = this.add(b0, B);
            c0 = this.add(c0, C);
            d0 = this.add(d0, D);
        }

        /* istanbul ignore next */
        if (paddingBytes.length !== 0) {
            throw new Error();
        }

        const result = [a0, b0, c0, d0].map(p => ToUInt32(Swap32(p)));
        const hash = ArrayToHEX(...result);

        return hash;
    }

    public async ComputeAsync(input: TAsyncHashInput): Promise<string> {
        // Note: All variables are unsigned 32 bit and wrap modulo 2^32 when calculating

        const data = InitializeAsyncDataAcquisition(input);

        // s specifies the per-round shift amounts
        const s = [
            7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
            5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
            4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
            6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
        ];

        // Use binary integer part of the sines of integers (Radians) as constants
        // const K: number[] = new Array(64);
        // for (let i = 0; i <= 63; i++) {
        //     K[i] = Math.floor(Math.pow(2, 32) * Math.abs(Math.sin(i + 1)));
        // }

        // Or just use the following precomputed table
        const K = [
            0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee,
            0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
            0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be,
            0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
            0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa,
            0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
            0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed,
            0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
            0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c,
            0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
            0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05,
            0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
            0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039,
            0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
            0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1,
            0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
        ];

        // Initialize variables
        let a0 = 0x67452301;
        let b0 = 0xefcdab89;
        let c0 = 0x98badcfe;
        let d0 = 0x10325476;

        /// Pre-processing

        const L = data.TotalLengthInBytes * 8;

        const paddingBytes: number[] = [];

        // append a single '1' bit, where the first bit is the most significant bit of the byte
        paddingBytes.push(0x80); // 8 bits

        // padding with zeros
        const total = 512 - (L % 512);
        let bitsCount = total - 8 - 64;
        // #38
        if (bitsCount < 0) {
            bitsCount += 512;
        }

        let n = bitsCount / 8;
        while (n-- > 0) {
            paddingBytes.push(0);
        }

        // append original length in bits
        const lbe = new BitString(L).ToBytes(64);
        paddingBytes.push(...lbe.reverse());

        const totalSize = data.TotalLengthInBytes + paddingBytes.length;

        /// Process the message in successive 512-bit chunks
        const totalChunks = totalSize / 64;

        for (let i = 0; i < totalChunks; i++) {
            const chunk = await data.AcquireAsync(i * 64, 64);

            while (chunk.length < 64) {
                chunk.push(paddingBytes.shift()!);
            }

            const M: number[] = new Array(64);

            // break chunk into sixteen 32-bit words M[j], 0 ≤ j ≤ 15
            const bit32Array = ArraySplit(chunk, 4, true);
            for (let j = 0; j < 16; j++) {
                const bits32 = bit32Array[j];
                M[j] = bits32[3] << 24 | bits32[2] << 16 | bits32[1] << 8 | bits32[0];
            }

            // Initialize hash value for this chunk
            let A = a0;
            let B = b0;
            let C = c0;
            let D = d0;

            // Main loop
            for (let i = 0; i <= 63; i++) {
                let F: number;
                let g: number;

                if (i >= 0 && i <= 15) {
                    // F := (B and C) or ((not B) and D)
                    F = (B & C) | ((~B) & D);

                    // g := i
                    g = i;
                } else if (i >= 16 && i <= 31) {
                    // F := (D and B) or ((not D) and C)
                    F = (D & B) | ((~D) & C);

                    // g := (5×i + 1) mod 16
                    g = (5 * i + 1) % 16;
                } else if (i >= 32 && i <= 47) {
                    // F := B xor C xor D
                    F = B ^ C ^ D;

                    // g := (3×i + 5) mod 16
                    g = (3 * i + 5) % 16;
                    // } else if (i >= 48 && i <= 63) {
                } else {
                    // F := C xor (B or (not D))
                    F = C ^ (B | (~D));

                    // g := (7×i) mod 16
                    g = (7 * i) % 16;
                }

                // Be wary of the below definitions of a,b,c,d
                F = this.addMany(F, A, K[i], M[g]);
                A = D;
                D = C;
                C = B;
                B = this.add(B, LeftRotate(F, s[i]));
            }

            // Add this chunk's hash to result so far
            a0 = this.add(a0, A);
            b0 = this.add(b0, B);
            c0 = this.add(c0, C);
            d0 = this.add(d0, D);
        }

        /* istanbul ignore next */
        if (paddingBytes.length !== 0) {
            throw new Error();
        }

        const result = [a0, b0, c0, d0].map(p => ToUInt32(Swap32(p)));
        const hash = ArrayToHEX(...result);

        return hash;
    }

    protected add(a: number, b: number) {
        return ToUInt32((ToUInt32(a) + ToUInt32(b)));
    }

    protected addMany(...items: number[]) {
        let val = 0;
        for (const item of items) {
            val = this.add(val, item);
        }

        return val;
    }
}
